import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from '@practium/features/auth';
import { ProtectedRoute, AppNavigationProvider } from '@practium/features/navigation';
import { ThemeProvider, ThemePreferenceProvider } from '@practium/features/theme';
import { ToastProvider, Unauthorized, LoadingState } from '@practium/ui';
import { FloraAIPage, FloraSupportWidget } from '@practium/features/flora-ai';
import { usePermissions } from '@practium/features/permissions';
import { UserRole, createGetAppUrl, normalizeHashRedirect } from '@practium/shared';
import { Layout } from './features/layout/Layout';
import { HomePageAdmin } from './features/home/pages/HomePageAdmin';
import { DashboardPage } from './features/dashboard/pages/DashboardPage';
import { ProfilePageAdmin } from './features/profile/pages/ProfilePageAdmin';
import { ProfileEditPageAdmin } from './features/profile/pages/ProfileEditPageAdmin';
import { OperationalPage } from './features/operational/pages/OperationalPage';
import { APP_ROUTES } from './config/app-routes.config';
import { LoginPageAdmin } from './features/auth/pages/LoginPageAdmin';
import { AuthExchangePageAdmin } from './features/auth/pages/AuthExchangePageAdmin';
import { ForgotPasswordPageAdmin } from './features/auth/pages/ForgotPasswordPageAdmin';
import { ResetPasswordPageAdmin } from './features/auth/pages/ResetPasswordPageAdmin';
import { NotificationProvider } from '@practium/features/profile';
import { PoliciesViewPage } from './features/policy/pages/PolicyViewPage';
import { PolicyEditPage } from './features/policy/pages/PolicyEditPage';
import { ParcellingPageAdmin } from './features/parcelling/pages/ParcellingPageAdmin';
import { PolicyNewPage } from './features/policy/pages/PolicyNewPage';
import { ParametersPage } from './features/parameters/pages/ParametersPage';
import { SettingsPage } from './features/settings/pages/SettingsPage';
import { PaymentGatewaysPage } from './features/settings/gateways/pages/PaymentGatewaysPage';
import { LandingControlPage } from './features/settings/landing/pages/LandingControlPage';
import { ViewLandingPlanPage } from './features/settings/landing/pages/ViewLandingPlanPage';
import { AddTestimonialPage } from './features/settings/landing/pages/AddTestimonialPage';
import { ViewTestimonialPage } from './features/settings/landing/pages/ViewTestimonialPage';
import { ProductsPageAdmin } from './features/products/pages/ProductsPageAdmin';
import { AddProductPageAdmin } from './features/products/pages/AddProductPageAdmin';
import { ViewProductPageAdmin } from './features/products/pages/ViewProductPageAdmin';
import { UsersPageAdmin } from './features/users/pages/UsersPageAdmin';
import { AddUserPageAdmin } from './features/users/pages/AddUserPageAdmin';
import { ViewUserPageAdmin } from './features/users/pages/ViewUserPageAdmin';
import { SupportPageAdmin } from './features/support/pages/SupportPageAdmin';
import { SystemPageAdmin } from './features/system/pages/SystemPageAdmin';
import { AddSystemPageAdmin } from './features/system/pages/AddSystemPageAdmin';
import { ViewSystemPageAdmin } from './features/system/pages/ViewSystemPageAdmin';
import { ProductRequestsPage } from './features/product-requests/pages/ProductRequestsPage';
import { LogsPage } from './features/logs/pages/LogsPage';
import { FieldDiaryPageAdmin } from './features/field-diary/FieldDiaryPageAdmin';
import { AnalysesPageAdmin } from './features/analyses/pages/AnalysesPageAdmin';
import { AddAnalysisPageAdmin } from './features/analyses/pages/AddAnalysisPageAdmin';
import { ViewAnalysisPageAdmin } from './features/analyses/pages/ViewAnalysisPageAdmin';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BillingPage } from './features/billing/pages/BillingPage';
import { ThemeDesignPage } from './features/settings/pages/ThemeDesignPage';

normalizeHashRedirect('/recuperar-senha');

function AppContent() {
	const { user } = useAuth();
	const { abilities, isLoading: permissionsLoading } = usePermissions({ enabled: !!user });

	const guard = (can: boolean, element: React.ReactElement) => {
		if (permissionsLoading) return <LoadingState fullScreen />;
		return can ? element : <Navigate to="/" replace />;
	};

	const canAccessProducts = abilities.can('read', 'products');
	const canAccessUsers = abilities.can('read', 'users');
	const canAccessSystem =
		abilities.can('read', 'groups') ||
		abilities.can('read', 'hierarchies') ||
		abilities.can('read', 'plans');
	const canAccessLanding =
		abilities.can('read', 'testimonials') || abilities.can('read', 'landingPlans');
	const canAccessSamples = abilities.can('read', 'samples');

	return (
		<>
			<Routes>
			<Route path="/login" element={<LoginPageAdmin />} />
			<Route path="/auth/exchange" element={<AuthExchangePageAdmin />} />
			<Route path="/forgot-password" element={<ForgotPasswordPageAdmin />} />
			<Route path="/recuperar-senha" element={<ResetPasswordPageAdmin />} />
			<Route path="/unauthorized" element={<Unauthorized />} />

			<Route
				element={
					<ProtectedRoute allowedRoles={[UserRole.Admin]}>
						<Layout />
					</ProtectedRoute>
				}
			>
				<Route path="/" element={<HomePageAdmin />} />
				<Route path="/dashboard" element={guard(abilities.can('read', 'dashboard'), <DashboardPage />)} />
				<Route path="/flora-ai" element={<FloraAIPage />} />
				<Route path="/produtos" element={guard(canAccessProducts, <ProductsPageAdmin />)} />
				<Route path="/produtos/novo" element={guard(canAccessProducts, <AddProductPageAdmin />)} />
				<Route path="/produtos/ver" element={guard(canAccessProducts, <ViewProductPageAdmin />)} />
				<Route path="/usuarios" element={guard(canAccessUsers, <UsersPageAdmin />)} />
				<Route path="/usuarios/novo" element={guard(canAccessUsers, <AddUserPageAdmin />)} />
				<Route path="/usuarios/ver" element={guard(canAccessUsers, <ViewUserPageAdmin />)} />
				<Route path="/solicitacoes" element={<ProductRequestsPage />} />
				<Route path="/logs" element={guard(abilities.can('read', 'logs'), <LogsPage />)} />
				<Route
					path="/suporte"
					element={guard(abilities.can('read', 'technical-support'), <SupportPageAdmin />)}
				/>
				<Route path="/sistema" element={guard(canAccessSystem, <SystemPageAdmin />)} />
				<Route path="/sistema/novo" element={<AddSystemPageAdmin />} />
				<Route path="/sistema/ver" element={<ViewSystemPageAdmin />} />
				<Route path="/perfil" element={<ProfilePageAdmin />} />
				<Route path="/perfil/editar" element={<ProfileEditPageAdmin />} />
				<Route path="/operacional" element={<OperationalPage />} />
				<Route path="/politicas" element={<PoliciesViewPage />} />
				<Route path="/politicas/:id" element={<PoliciesViewPage />} />
				<Route path="/politicas/nova" element={<PolicyNewPage />} />
				<Route path="/politicas/editar/:id" element={<PolicyEditPage />} />
				<Route
					path="/parcelamento"
					element={guard(abilities.can('read', 'parcelling'), <ParcellingPageAdmin />)}
				/>
				<Route path="/parametros" element={<ParametersPage />} />
				<Route path="/configuracoes" element={<SettingsPage />} />
				<Route path="/configuracoes/design" element={<ThemeDesignPage />} />
				<Route path="/configuracoes/gateways" element={<PaymentGatewaysPage />} />
				<Route
					path="/configuracoes/landing"
					element={guard(canAccessLanding, <LandingControlPage />)}
				/>
				<Route
					path="/configuracoes/landing/planos/ver"
					element={guard(canAccessLanding, <ViewLandingPlanPage />)}
				/>
				<Route
					path="/configuracoes/landing/depoimentos/novo"
					element={guard(canAccessLanding, <AddTestimonialPage />)}
				/>
				<Route
					path="/configuracoes/landing/depoimentos/ver"
					element={guard(canAccessLanding, <ViewTestimonialPage />)}
				/>
				<Route path="/diario-de-campo" element={<FieldDiaryPageAdmin />} />
				<Route path="/analises" element={guard(canAccessSamples, <AnalysesPageAdmin />)} />
				<Route path="/analises/novo" element={guard(canAccessSamples, <AddAnalysisPageAdmin />)} />
				<Route path="/analises/ver" element={guard(canAccessSamples, <ViewAnalysisPageAdmin />)} />
				<Route path="/faturamento" element={guard(abilities.can('read', 'payments'), <BillingPage />)} />
			</Route>

			<Route path="*" element={<Navigate to="/dashboard" replace />} />
			</Routes>
			<FloraSupportWidget />
		</>
	);
}

const queryClient = new QueryClient();

function App() {
	const getAppUrl = createGetAppUrl(APP_ROUTES);
	const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

	const content = (
		<ThemeProvider>
			<AuthProvider>
				<QueryClientProvider client={queryClient}>
					<AppNavigationProvider config={{ getAppUrl }}>
						<ToastProvider>
							<ThemePreferenceProvider>
								<BrowserRouter>
									<NotificationProvider>
										<AppContent />
									</NotificationProvider>
								</BrowserRouter>
							</ThemePreferenceProvider>
						</ToastProvider>
					</AppNavigationProvider>
				</QueryClientProvider>
			</AuthProvider>
		</ThemeProvider>
	);

	return googleClientId ? (
		<GoogleOAuthProvider clientId={googleClientId}>{content}</GoogleOAuthProvider>
	) : (
		content
	);
}

export default App;
